import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useToast } from '../../hooks/useToast';
import { Modal } from '../common/Modal';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Textarea } from '../common/Textarea';
import { ImageIcon } from '@radix-ui/react-icons';
import { submitFeedback } from '../../api/feedback';

const FeedbackModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && !file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please upload an image file",
        type: "error"
      });
      return;
    }
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());

      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      await submitFeedback(formDataToSend);

      toast({
        title: "Success",
        description: "Thank you for your feedback!",
        type: "success"
      });
      onClose();
      setFormData({ title: '', description: '', image: null });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to submit feedback",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4" encType="multipart/form-data">
        <Modal.Header>
          <Modal.Title>Send Feedback</Modal.Title>
          <Modal.Description>
            Help us improve by sharing your thoughts and suggestions
          </Modal.Description>
        </Modal.Header>

        <div className="space-y-4">
          <div>
            <Input
              type="text"
              name="title"
              placeholder="Title"
              value={formData.title}
              onChange={handleChange}
              required
              maxLength={200}
              className="w-full"
            />
          </div>
          <div>
            <Textarea
              name="description"
              placeholder="Describe your feedback..."
              value={formData.description}
              onChange={handleChange}
              required
              className="w-full min-h-[120px]"
            />
          </div>
          <div>
            <label className="block">
              <div className="flex items-center gap-2 p-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-accent transition-colors">
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {formData.image ? formData.image.name : 'Upload an image (optional)'}
                </span>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </label>
          </div>
        </div>

        <Modal.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || !formData.title.trim() || !formData.description.trim() || formData.title.length > 200}
          >
            {loading ? (
              <motion.div
                className="h-5 w-5 border-2 border-current border-r-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default FeedbackModal; 